import { ChangeDetectorRef, Component, computed, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { FuseNavigationItem } from '@fuse/types';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { SharedService } from '@fuse/services/shared.service';
import { User } from 'app/core/models/user';
import { ALNIdentityAuthService } from 'app/core/auth/aln-identity-auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { NewsCountService, NEWS_COUNT_NAV_ITEM_ID } from 'app/core/services/news-count.service';

@Component({
    selector: 'fuse-nav-vertical-item',
    templateUrl: './item.component.html',
    styleUrls: ['./item.component.scss'],
    standalone: false
})
export class FuseNavVerticalItemComponent implements OnInit, OnDestroy {
    @HostBinding('class')
    classes = 'nav-item';
    @Input()
    item: FuseNavigationItem;
    currentUser: User;
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    private _unsubscribeAll: Subject<any>;
    listofProducts: Array<any> = [];

    /**
     * Live news count for this item, or null when it has none.
     *
     * Only the Information Center item shows it. Reading it from a signal is what
     * makes the badge repaint: the count arrives on a window event fired outside
     * Angular's zone, and a signal read in the template gets its own change
     * detection scheduled when the value changes.
     */
    readonly newsCount = computed<number | null>(() =>
        this.item?.id === NEWS_COUNT_NAV_ITEM_ID ? this._newsCountService.count() : null
    );

    /**
     * Constructor
     */

    /**
     *
     * @param {ChangeDetectorRef} _changeDetectorRef
     * @param {FuseNavigationService} _fuseNavigationService
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        public _data: SharedService,
        private authenticationService: ALNIdentityAuthService,
        private router: Router,
        public dialog: MatDialog,
        private _newsCountService: NewsCountService
    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.checkAccessForeDockets();

        // Subscribe to navigation item
        merge(
            this._fuseNavigationService.onNavigationItemAdded,
            this._fuseNavigationService.onNavigationItemUpdated,
            this._fuseNavigationService.onNavigationItemRemoved
        ).pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Re-evaluate active state on navigation changes
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });

    }

    checkAccessForeDockets() {
        var _allProducts = sessionStorage.getItem('allProducts');
        if (_allProducts) {
            var allClients = JSON.parse(_allProducts);
            var edocketProd = allClients.filter(x => x.clientId == 'eDockets');
            if (edocketProd.length > 0 && !edocketProd[0].isSubscribed) {
                this.router.events.subscribe((e) => {
                    if (e instanceof NavigationEnd) {
                        if (e.url && (e.url.includes('/case-management')
                            || e.url.includes('/dashboard')
                            || e.url.includes('eDockets')
                            || e.url.includes('admin-management')
                            || e.url.includes('autodocket')
                            || e.url.includes('docketalerts'))) {
                            this._data.sendMessage(true);
                            return;
                        }
                    }
                });
            }
        }
    }

    checkAccess() {
        let isFormChanged = sessionStorage.getItem("isFormChanged");
        if (!!isFormChanged) {
            this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, { disableClose: false });
            this.confirmDialogRef.componentInstance.confirmMessage = 'You have unsaved changes.  Do you wish to continue WITHOUT saving your changes?';;
            this.confirmDialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.formvalidation();
                    sessionStorage.removeItem("isFormChanged");
                } else {
                    let element = document.getElementById('Item-case-management');
                    element.classList.add('accent');
                }
            })
        }
        else {
            this.formvalidation();
        }
    }

    formvalidation() {
        this.currentUser = this.authenticationService.user;
        if (this.item.code == 'mail') {
            this.item.url = `${environment.centralAppBaseUri}/apps/appgateway/${this.currentUser.userId}/mail`;
        }
        else if (this.item.code == 'calendar') {
            this.item.url = `${environment.centralAppBaseUri}/apps/appgateway/${this.currentUser.userId}/calendar`;
        }
        else {
            if (!this.item.openInNewTab) {
                this._data.sendMessage(this.verifyProductAccess(this.item.code));
            }
        }
        this.router.navigateByUrl(this.item.url);
        if (this.item.url === "/") {
            // Set the main navigation as our current navigation
            this._fuseNavigationService.setCurrentNavigation('main');
        }

        if (this.item.url === "/docketalerts") {
            // Set the docketAlert navigation as our current navigation
            this._fuseNavigationService.setCurrentNavigation('docketAlerts');
        }

        let element = document.getElementsByClassName("shell-app-link");
        if (element.length != 0) {
            for (var i = 0; i <= element.length - 1; i++) {
                if (element[i].classList.contains('accent')) {
                    element[i].classList.remove('accent');
                }
            }
            let appLink = document.getElementById("Item-" + this.item.id);
            if (appLink) {
                appLink.classList.add('accent');
            }
        }
    }
    verifyProductAccess(productId): any {
        this.listofProducts = [];
        var _user = sessionStorage.getItem('userProducts');
        if (_user) {
            this.listofProducts = JSON.parse(_user);
            if (this.listofProducts.filter(x => x.clientCode == productId).length == 0) {
                return true;
            }
        }
        return false;
    }

    isActive(url): boolean {
        if (!url) return false;
        if (url === '/') {
            return window.location.pathname === '/';
        }
        const pathname = window.location.pathname.toLowerCase();
        const itemUrl = url.toLowerCase();
        const segments = itemUrl.split('/').filter(Boolean);
        // Build a match prefix from up to the first 2 segments of the item URL
        // This distinguishes /milana/calendar from /milana/quickdates
        // while still matching /case-management/cm/home with /case-management/cm/details
        const matchCount = Math.min(segments.length, 2);
        const matchPrefix = '/' + segments.slice(0, matchCount).join('/');
        if (pathname === matchPrefix || pathname.startsWith(matchPrefix + '/')) {
            return true;
        }
        // Fallback: match when current path is just the app's base segment
        // (e.g., /case-management before micro-frontend routes to /case-management/cm/home)
        // Only applies to 2-segment URLs — 3-segment URLs like /case-management/ic/home have a
        // distinct second segment that already differentiates them, so the broad base fallback
        // would incorrectly highlight them alongside other /case-management/* items.
        if (segments.length === 2) {
            const basePath = '/' + segments[0];
            if (pathname === basePath) {
                return true;
            }
        }
        return false;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
